import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import { SeismographService } from '../../services/seismograph-service';
import { Seismograph } from '../../../../shared/models/seismograph';
import { Company } from '../../../../core/models/company';
import { FonctionsService } from '../../../../shared/services/utilities/fonctions.service';
import { TranslateModule } from '@ngx-translate/core';
import { TimeZoneDto } from '@shared/models/time-zone.dto';
import { NgSelectModule } from '@ng-select/ng-select';

interface DeviceForm {
  serialNumber: FormControl<string>;
  selectedCompanyId: FormControl<number | null>;
  favorite: FormControl<boolean>;
  lastCalibration: FormControl<Date | string | null>;
  loginName: FormControl<string | null>;
  password: FormControl<string | null>;
  confirmPassword: FormControl<string | null>;
}

@Component({
  selector: 'app-add-device-modal',
  standalone: true,
  imports: [TranslateModule, CommonModule, ReactiveFormsModule, NgSelectModule ],
  templateUrl: './add-device-modal.component.html',
  styleUrls: ['./add-device-modal.component.scss'],
})
export class NewDeviceModalComponent implements OnInit {
  deviceForm: FormGroup<any>;
  sitesList: any[] = [];
  companiesList: Company[] = [];
  timeZones: TimeZoneDto[] = [];
  isEdit: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<NewDeviceModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private seismographService: SeismographService,
    private fonctionsService: FonctionsService) {
    this.deviceForm = this.fb.group({
      serialNumber: this.fb.control('', Validators.required),
      selectedCompanyId: this.fb.control(null, Validators.required),
      favorite: this.fb.control(false),
      lastCalibration: this.fb.control<Date | string | null>(null),
      siteId: this.fb.control(null),
      timeZoneId: this.fb.control<number | null>(null)
    });
  }

  async ngOnInit(): Promise<void> {
  const allSites = localStorage.getItem('allSites');
  this.sitesList = allSites ? JSON.parse(allSites) : [];
    try {
      this.isEdit = this.data?.isEdit || false;
      this.companiesList = (await this.seismographService.getAllCompanies()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      this.timeZones = (await this.seismographService.getTimeZones()).sort((a, b) =>
        (a.displayName || a.name || '').localeCompare((b.displayName || b.name || ''))
      );
    } catch (error) {
      console.error('Error fetching companies list:', error);
    }

    if (this.data.device) {
      this.deviceForm.patchValue({
        serialNumber: this.data.device.serialNumber,
        favorite: this.data.device.isFavorite,
        lastCalibration: this.data.device.lastCalibration ?? null,
        siteId: this.data.device.siteId,
        timeZoneId: this.data.device.timeZoneId ?? null
      });
      this.deviceForm.get('selectedCompanyId')?.setValue(this.data.device.companyId);
    }
  }

  async confirm(): Promise<void> {
    if (this.deviceForm.get('serialNumber')?.invalid || this.deviceForm.get('selectedCompanyId')?.invalid) {
      this.deviceForm.markAllAsTouched();
      return;
    }

    const { serialNumber, selectedCompanyId, favorite, lastCalibration, siteId, timeZoneId } = this.deviceForm.value;
    const newDevice = new Seismograph();
    if (this.data.device?.id) {
      newDevice.id = this.data.device.id;
    }
    newDevice.serialNumber = serialNumber!;
    newDevice.isFavorite = favorite!;
    newDevice.companyId = parseInt(selectedCompanyId.toString()) || 0;
    newDevice.siteId = siteId || undefined;
    newDevice.timeZoneId = timeZoneId != null ? Number(timeZoneId) : undefined;
    newDevice.lastCalibration = lastCalibration ?? this.data.device?.lastCalibration ?? undefined;
    try {
      if (this.isEdit) {
        await this.seismographService.updateSeismograph(newDevice);
        this.dialogRef.close(newDevice);
        this.fonctionsService.launch_toast(true, 'Device successfully updated');
      } else {
        newDevice.id = 0
        await this.seismographService.createSeismograph(newDevice);
        this.dialogRef.close(newDevice);
        this.fonctionsService.launch_toast(true, 'Device successfully created');
      }
    } catch (error) {
      this.fonctionsService.launch_toast(false, 'Error creating device');
    }
  }


  closeDialog(): void {
    this.dialogRef.close(null);
  }

  onDropdownSelected(): void {
    const active = document.activeElement as HTMLElement | null;
    active?.blur();
  }
}